import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  input,
  output,
  signal,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import {
  IFilterPanelSelection,
  IFilterPanelValues,
  IManagementListColumn,
  IManagementListItem,
  IPageChange,
} from 'src/app/models';
import { APP } from 'src/app/config/app';
import { FilterField, FilterType } from 'src/app/models/enums';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { IPagination } from 'src/app/store/states/pagination';
import { images } from 'src/images';
import { FilterPanelComponent } from '../filter-panel/filter-panel.component';
import { InputSearchComponent } from '../input/input-search/input-search.component';
import { PaginationComponent } from '../pagination/pagination.component';

@Component({
  selector: 'app-management-list',
  templateUrl: './management-list.component.html',
  styleUrls: ['./management-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgTemplateOutlet, NgxSkeletonLoaderModule, FilterPanelComponent, InputSearchComponent, PaginationComponent],
})
export class ManagementListComponent<T extends IManagementListItem> {
  readonly searchPlaceholder = input.required<string>();
  readonly createLabel = input.required<string>();
  readonly createIcon = input(images.plus);
  readonly searchValue = input('');
  readonly columns = input.required<IManagementListColumn[]>();
  readonly items = input.required<T[]>();
  readonly selectedId = input<number>();
  readonly pagination = input.required<IPagination>();
  readonly loading = input(false);
  readonly emptyText = input.required<string>();
  readonly filterType = input<FilterType>();
  readonly filterFields = input<FilterField[]>([]);
  readonly filterSelection = input<IFilterPanelSelection | null>(null);
  readonly filtersApplied = input(0);

  readonly search = output<string>();
  readonly create = output();
  readonly changePage = output<IPageChange>();
  readonly selectItem = output<T>();
  readonly filter = output<IFilterPanelValues>();

  readonly rowTemplate = contentChild.required<TemplateRef<{ $implicit: T }>>('row');

  readonly images = images;
  readonly filterOpened = signal(false);
  readonly gridTemplateColumns = computed(() => {
    const totalSpan = this.columns().reduce((total, column) => total + column.span, 0);
    return `repeat(${totalSpan}, minmax(0, 1fr))`;
  });
  readonly skeletonRows = computed(() => this.pagination().size || APP.DEFAULT_PAGE_SIZE);
  readonly columnHeaders = computed(() =>
    this.columns().map((column) => ({ ...column, gridColumn: `span ${column.span}` })),
  );
  readonly empty = computed(() => !this.loading() && this.items().length === 0);

  constructor(public literalService: LiteralService) {}

  onFilter(values: IFilterPanelValues) {
    this.filter.emit(values);
  }
}
